import {
  ServiceItem,
  BundleItem,
  CategoryItem,
  SERVICES_LIST as DEFAULT_SERVICES,
  BLISHKA_BUNDLES as DEFAULT_BUNDLES,
  CATEGORIES as DEFAULT_CATEGORIES
} from '../data/services';

export type { ServiceItem, BundleItem, CategoryItem };

const SERVICES_STORAGE_KEY = 'blishka_services_catalog_v6';
const BUNDLES_STORAGE_KEY = 'blishka_bundles_catalog_v1';
const CATEGORIES_STORAGE_KEY = 'blishka_categories_catalog_v3';
const DELETED_CATEGORIES_KEY = 'blishka_deleted_categories_v2';
const CATALOG_UPDATE_EVENT = 'blishka_catalog_updated';

export function getDeletedCategories(): string[] {
  const fallback = ['scrubbing', 'massage-scrub', 'massage'];
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(DELETED_CATEGORIES_KEY);
    if (!raw) {
      localStorage.setItem(DELETED_CATEGORIES_KEY, JSON.stringify(fallback));
      return fallback;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // Ensure relaxing-massage is un-deleted
      const filtered = parsed.filter((c) => c !== 'relaxing-massage');
      if (filtered.length !== parsed.length) {
        localStorage.setItem(DELETED_CATEGORIES_KEY, JSON.stringify(filtered));
      }
      return filtered;
    }
  } catch {
    // fallback
  }
  return fallback;
}

function getStoredAdminPin(): string {
  if (typeof window === 'undefined') return '7994';
  return sessionStorage.getItem('blishka_admin_pin') || '7994';
}

function emitUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CATALOG_UPDATE_EVENT));
  }
}

/**
 * Returns the active services list from localStorage, or seeds from DEFAULT_SERVICES.
 * Synchronous for instant React renders.
 */
export function getServicesList(): ServiceItem[] {
  const deleted = getDeletedCategories();
  if (typeof window === 'undefined') {
    return DEFAULT_SERVICES.filter((s) => !deleted.includes(s.category.toLowerCase()));
  }
  try {
    const raw = localStorage.getItem(SERVICES_STORAGE_KEY);
    if (!raw) {
      const initial = DEFAULT_SERVICES.filter((s) => !deleted.includes(s.category.toLowerCase()));
      localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(initial));
      return initial;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      // Ensure any newly active default services (like relaxing-massage) are included
      const existingIds = new Set(parsed.map((s: ServiceItem) => s.id));
      for (const defService of DEFAULT_SERVICES) {
        if (!existingIds.has(defService.id) && !deleted.includes(defService.category.toLowerCase())) {
          parsed.push(defService);
        }
      }
      for (const item of parsed) {
        if (item.durationMinutes === undefined) {
          const def = DEFAULT_SERVICES.find(d => d.id === item.id);
          item.durationMinutes = def?.durationMinutes || 30;
        }
      }
      return parsed.filter((s: ServiceItem) => !deleted.includes(s.category.toLowerCase()));
    }
    return DEFAULT_SERVICES.filter((s) => !deleted.includes(s.category.toLowerCase()));
  } catch (err) {
    console.error('Failed to read services catalog from localStorage:', err);
    return DEFAULT_SERVICES.filter((s) => !deleted.includes(s.category.toLowerCase()));
  }
}

/**
 * Fetches the latest services catalog from Cloudflare D1 database via /api/services
 * and syncs it into localStorage and notifies all UI components.
 */
export async function syncServicesFromRemote(adminPin?: string): Promise<{
  success: boolean;
  services: ServiceItem[];
  isD1Connected: boolean;
}> {
  try {
    const headers: Record<string, string> = {
      'Cache-Control': 'no-store'
    };
    const pin = adminPin || getStoredAdminPin();
    if (pin) {
      headers['x-admin-pin'] = pin;
    }

    const res = await fetch('/api/services', { headers });
    if (res.ok) {
      const data = await res.json() as {
        success?: boolean;
        services?: ServiceItem[];
        isD1Connected?: boolean;
      };

      if (data && Array.isArray(data.services) && data.services.length > 0) {
        const deleted = getDeletedCategories();
        const cleaned = data.services.filter(s => !deleted.includes(s.category.toLowerCase()));
        localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(cleaned));
        emitUpdate();
        return {
          success: true,
          services: cleaned,
          isD1Connected: !!data.isD1Connected
        };
      }
    }
  } catch (err) {
    console.warn('Could not sync services catalog with remote Cloudflare D1:', err);
  }

  return {
    success: false,
    services: getServicesList(),
    isD1Connected: false
  };
}

/**
 * Adds or updates a service item:
 * 1. Immediately updates localStorage (optimistic UI update)
 * 2. Emits app-wide update event
 * 3. Asynchronously persists to Cloudflare D1 database via /api/services
 */
export async function saveService(service: ServiceItem, adminPin?: string): Promise<boolean> {
  // 1. Optimistic local update
  try {
    const current = getServicesList();
    const index = current.findIndex((s) => s.id === service.id);
    let updated: ServiceItem[];
    if (index >= 0) {
      updated = [...current];
      updated[index] = { ...updated[index], ...service };
    } else {
      updated = [service, ...current];
    }
    localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(updated));
    emitUpdate();
  } catch (err) {
    console.error('Failed to save service locally:', err);
  }

  // 2. Persist to Cloudflare D1 database
  try {
    const pin = adminPin || getStoredAdminPin();
    const res = await fetch('/api/services', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': pin
      },
      body: JSON.stringify(service)
    });

    if (!res.ok) {
      console.warn('Failed to sync saved service to Cloudflare D1, status:', res.status);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('Offline or local preview: service saved locally only:', err);
    return false;
  }
}

/**
 * Updates just the price (and optionally originalPrice) of a service:
 * 1. Optimistically updates localStorage
 * 2. Asynchronously persists to Cloudflare D1
 */
export async function updateServicePrice(
  id: string,
  price: number,
  originalPrice?: number,
  adminPin?: string
): Promise<boolean> {
  const current = getServicesList();
  const index = current.findIndex((s) => s.id === id);
  if (index >= 0) {
    const item = current[index];
    const newOrig = originalPrice !== undefined ? originalPrice : item.originalPrice;
    const updatedItem: ServiceItem = {
      ...item,
      price: Math.max(0, price),
      originalPrice: Math.max(0, newOrig)
    };
    return await saveService(updatedItem, adminPin);
  }
  return false;
}

/**
 * Deletes a service item by ID:
 * 1. Immediately updates localStorage
 * 2. Asynchronously deletes from Cloudflare D1
 */
export async function deleteService(id: string, adminPin?: string): Promise<boolean> {
  // 1. Optimistic local delete
  try {
    const current = getServicesList();
    const updated = current.filter((s) => s.id !== id);
    localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(updated));
    emitUpdate();
  } catch (err) {
    console.error('Failed to delete service locally:', err);
  }

  // 2. Remote D1 delete
  try {
    const pin = adminPin || getStoredAdminPin();
    const res = await fetch(`/api/services?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        'x-admin-pin': pin
      }
    });
    return res.ok;
  } catch (err) {
    console.warn('Failed to sync service deletion to Cloudflare D1:', err);
    return false;
  }
}

/**
 * Returns the active curated salon bundles from localStorage, or seeds from DEFAULT_BUNDLES.
 */
export function getBundlesList(): BundleItem[] {
  if (typeof window === 'undefined') return DEFAULT_BUNDLES;
  try {
    const raw = localStorage.getItem(BUNDLES_STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(BUNDLES_STORAGE_KEY, JSON.stringify(DEFAULT_BUNDLES));
      return DEFAULT_BUNDLES;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed;
    }
    return DEFAULT_BUNDLES;
  } catch (err) {
    console.error('Failed to read bundles from localStorage:', err);
    return DEFAULT_BUNDLES;
  }
}

/**
 * Saves or updates a bundle in local storage.
 */
export function saveBundle(bundle: BundleItem): void {
  try {
    const current = getBundlesList();
    const index = current.findIndex((b) => b.id === bundle.id);
    let updated: BundleItem[];
    if (index >= 0) {
      updated = [...current];
      updated[index] = { ...updated[index], ...bundle };
    } else {
      updated = [bundle, ...current];
    }
    localStorage.setItem(BUNDLES_STORAGE_KEY, JSON.stringify(updated));
    emitUpdate();
  } catch (err) {
    console.error('Failed to save bundle locally:', err);
  }
}

/**
 * Deletes a bundle from local storage.
 */
export function deleteBundle(id: string): void {
  try {
    const current = getBundlesList();
    const updated = current.filter((b) => b.id !== id);
    localStorage.setItem(BUNDLES_STORAGE_KEY, JSON.stringify(updated));
    emitUpdate();
  } catch (err) {
    console.error('Failed to delete bundle locally:', err);
  }
}

/**
 * Fetches the latest bundles from Cloudflare D1 via /api/bundles
 * and syncs into localStorage.
 */
export async function syncBundlesFromRemote(adminPin?: string): Promise<{
  success: boolean;
  bundles: BundleItem[];
  isD1Connected: boolean;
}> {
  try {
    const headers: Record<string, string> = { 'Cache-Control': 'no-store' };
    const pin = adminPin || getStoredAdminPin();
    if (pin) headers['x-admin-pin'] = pin;

    const res = await fetch('/api/bundles', { headers });
    if (res.ok) {
      const data = await res.json() as {
        success?: boolean;
        bundles?: BundleItem[];
        isD1Connected?: boolean;
      };
      if (data && Array.isArray(data.bundles) && data.bundles.length > 0) {
        localStorage.setItem(BUNDLES_STORAGE_KEY, JSON.stringify(data.bundles));
        emitUpdate();
        return {
          success: true,
          bundles: data.bundles,
          isD1Connected: !!data.isD1Connected
        };
      }
    }
  } catch (err) {
    console.warn('Could not sync bundles from Cloudflare D1:', err);
  }
  return {
    success: false,
    bundles: getBundlesList(),
    isD1Connected: false
  };
}

/**
 * Adds or updates a bundle item locally and persists to Cloudflare D1.
 */
export async function saveBundleRemote(bundle: BundleItem, adminPin?: string): Promise<boolean> {
  // 1. Optimistic local update
  saveBundle(bundle);

  // 2. Persist to D1
  try {
    const pin = adminPin || getStoredAdminPin();
    const res = await fetch('/api/bundles', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': pin
      },
      body: JSON.stringify(bundle)
    });
    return res.ok;
  } catch (err) {
    console.warn('Offline or error saving bundle to Cloudflare D1:', err);
    return false;
  }
}

/**
 * Deletes a bundle item by ID locally and from Cloudflare D1.
 */
export async function deleteBundleRemote(id: string, adminPin?: string): Promise<boolean> {
  // 1. Optimistic local delete
  deleteBundle(id);

  // 2. Remote D1 delete
  try {
    const pin = adminPin || getStoredAdminPin();
    const res = await fetch(`/api/bundles?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        'x-admin-pin': pin
      }
    });
    return res.ok;
  } catch (err) {
    console.warn('Failed to sync bundle deletion to Cloudflare D1:', err);
    return false;
  }
}

/**
 * Returns the active category list from localStorage, or seeds from DEFAULT_CATEGORIES.
 */
export function getCategoriesList(): CategoryItem[] {
  const deleted = getDeletedCategories();
  if (typeof window === 'undefined') {
    return DEFAULT_CATEGORIES.filter((c) => c.id !== 'bundles' && !deleted.includes(c.id.toLowerCase()));
  }
  try {
    const raw = localStorage.getItem(CATEGORIES_STORAGE_KEY);
    if (!raw) {
      // Migrate from legacy v2 storage if available
      const legacyRaw = localStorage.getItem('blishka_categories_catalog_v2');
      let baseList = [...DEFAULT_CATEGORIES];
      if (legacyRaw) {
        try {
          const legacyParsed = JSON.parse(legacyRaw);
          if (Array.isArray(legacyParsed) && legacyParsed.length > 0) {
            baseList = legacyParsed;
          }
        } catch {}
      }
      const initial = baseList.filter((c) => c.id !== 'bundles' && !deleted.includes(c.id.toLowerCase()));
      localStorage.setItem(CATEGORIES_STORAGE_KEY, JSON.stringify(initial));
      return [...initial];
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      // Ensure newly active default categories (like relaxing-massage) are included
      const existingCatIds = new Set(parsed.map((c: CategoryItem) => c.id));
      for (const defCat of DEFAULT_CATEGORIES) {
        if (!existingCatIds.has(defCat.id) && !deleted.includes(defCat.id.toLowerCase())) {
          parsed.push(defCat);
        }
      }
      return parsed.filter((c: CategoryItem) => c.id !== 'bundles' && !deleted.includes(c.id.toLowerCase()));
    }
    return DEFAULT_CATEGORIES.filter((c) => c.id !== 'bundles' && !deleted.includes(c.id.toLowerCase()));
  } catch (err) {
    console.error('Failed to read categories from localStorage:', err);
    return DEFAULT_CATEGORIES.filter((c) => c.id !== 'bundles' && !deleted.includes(c.id.toLowerCase()));
  }
}

/**
 * Adds or updates a category item.
 */
export function saveCategory(category: CategoryItem): void {
  try {
    // Un-delete if this category was previously marked deleted
    const deleted = getDeletedCategories();
    if (deleted.includes(category.id.toLowerCase())) {
      const remaining = deleted.filter(d => d !== category.id.toLowerCase());
      localStorage.setItem(DELETED_CATEGORIES_KEY, JSON.stringify(remaining));
    }

    const current = getCategoriesList();
    const index = current.findIndex((c) => c.id.toLowerCase() === category.id.toLowerCase());
    let updated: CategoryItem[];
    if (index >= 0) {
      updated = [...current];
      updated[index] = { ...updated[index], ...category };
    } else {
      updated = [...current, category];
    }
    localStorage.setItem(CATEGORIES_STORAGE_KEY, JSON.stringify(updated));
    emitUpdate();
  } catch (err) {
    console.error('Failed to save category:', err);
  }
}

/**
 * Deletes a category item locally and purges its services from localStorage.
 */
export function deleteCategory(id: string): { success: boolean; error?: string } {
  if (id === 'all') {
    return { success: false, error: 'Cannot delete default system category' };
  }
  try {
    // 1. Mark as deleted permanently
    const deleted = getDeletedCategories();
    if (!deleted.includes(id.toLowerCase())) {
      deleted.push(id.toLowerCase());
      localStorage.setItem(DELETED_CATEGORIES_KEY, JSON.stringify(deleted));
    }

    // 2. Remove category
    const current = getCategoriesList();
    const updated = current.filter((c) => c.id.toLowerCase() !== id.toLowerCase());
    localStorage.setItem(CATEGORIES_STORAGE_KEY, JSON.stringify(updated));

    // 3. Purge all services in this category locally
    const currentServices = getServicesList();
    const updatedServices = currentServices.filter((s) => s.category.toLowerCase() !== id.toLowerCase());
    localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(updatedServices));

    emitUpdate();
    return { success: true };
  } catch (err) {
    console.error('Failed to delete category:', err);
    return { success: false, error: 'Storage error' };
  }
}

/**
 * Fetches the latest categories from Cloudflare D1 via /api/categories
 * and syncs into localStorage.
 */
export async function syncCategoriesFromRemote(adminPin?: string): Promise<{
  success: boolean;
  categories: CategoryItem[];
  isD1Connected: boolean;
}> {
  try {
    const headers: Record<string, string> = { 'Cache-Control': 'no-store' };
    const pin = adminPin || getStoredAdminPin();
    if (pin) headers['x-admin-pin'] = pin;

    const res = await fetch('/api/categories', { headers });
    if (res.ok) {
      const data = await res.json() as {
        success?: boolean;
        categories?: CategoryItem[];
        isD1Connected?: boolean;
      };
      if (data && Array.isArray(data.categories) && data.categories.length > 0) {
        const deleted = getDeletedCategories();
        const cleaned = data.categories.filter(c => !deleted.includes(c.id.toLowerCase()));
        localStorage.setItem(CATEGORIES_STORAGE_KEY, JSON.stringify(cleaned));
        emitUpdate();
        return {
          success: true,
          categories: cleaned,
          isD1Connected: !!data.isD1Connected
        };
      }
    }
  } catch (err) {
    console.warn('Could not sync categories from Cloudflare D1:', err);
  }
  return {
    success: false,
    categories: getCategoriesList(),
    isD1Connected: false
  };
}

/**
 * Adds or updates a category item locally and persists to Cloudflare D1.
 */
export async function saveCategoryRemote(category: CategoryItem, adminPin?: string): Promise<boolean> {
  // 1. Optimistic local update
  saveCategory(category);

  // 2. Persist to D1
  try {
    const pin = adminPin || getStoredAdminPin();
    const res = await fetch('/api/categories', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': pin
      },
      body: JSON.stringify(category)
    });
    return res.ok;
  } catch (err) {
    console.warn('Offline or error saving category to Cloudflare D1:', err);
    return false;
  }
}

/**
 * Deletes a category item and cascades delete to Cloudflare D1 database.
 * Deletes all services in this category from D1 and localStorage.
 */
export async function deleteCategoryRemote(
  id: string,
  adminPin?: string
): Promise<{ success: boolean; error?: string }> {
  if (id === 'all') {
    return { success: false, error: 'Cannot delete default system category' };
  }

  // 1. Optimistic local delete (category + all associated services)
  const localRes = deleteCategory(id);
  if (!localRes.success) {
    return localRes;
  }

  // 2. Remote D1 database delete: remove category and its services via /api/categories
  try {
    const pin = adminPin || getStoredAdminPin();
    const res = await fetch(`/api/categories?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        'x-admin-pin': pin
      }
    });

    if (!res.ok) {
      console.warn('Remote D1 category deletion responded with status:', res.status);
    }
    return { success: true };
  } catch (err) {
    console.warn('Network issue during remote category delete; local changes saved:', err);
    return { success: true };
  }
}

/**
 * Master sync function that syncs services, categories, and bundles in parallel.
 */
export async function syncAllCatalogFromRemote(adminPin?: string): Promise<{
  services: boolean;
  categories: boolean;
  bundles: boolean;
  isD1Connected: boolean;
}> {
  const [srvRes, catRes, bndRes] = await Promise.all([
    syncServicesFromRemote(adminPin).catch(() => ({ success: false, isD1Connected: false })),
    syncCategoriesFromRemote(adminPin).catch(() => ({ success: false, isD1Connected: false })),
    syncBundlesFromRemote(adminPin).catch(() => ({ success: false, isD1Connected: false }))
  ]);

  return {
    services: !!srvRes.success,
    categories: !!catRes.success,
    bundles: !!bndRes.success,
    isD1Connected: !!(srvRes.isD1Connected || catRes.isD1Connected || bndRes.isD1Connected)
  };
}

/**
 * Resets services, bundles, and categories back to default factory catalog.
 * Also resets remote Cloudflare D1 services if adminPin provided.
 */
export async function resetCatalogToDefaults(adminPin?: string): Promise<void> {
  try {
    localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(DEFAULT_SERVICES));
    localStorage.setItem(BUNDLES_STORAGE_KEY, JSON.stringify(DEFAULT_BUNDLES));
    localStorage.setItem(CATEGORIES_STORAGE_KEY, JSON.stringify(DEFAULT_CATEGORIES));
    emitUpdate();

    const pin = adminPin || getStoredAdminPin();
    if (pin) {
      await fetch('/api/admin/services', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-pin': pin
        },
        body: JSON.stringify({ action: 'reset_to_defaults' })
      }).catch(() => {});
    }
  } catch (err) {
    console.error('Failed to reset catalog to defaults:', err);
  }
}

/**
 * Subscribe to catalog changes across the app.
 */
export function subscribeToCatalogUpdates(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => callback();
  window.addEventListener(CATALOG_UPDATE_EVENT, handler);
  window.addEventListener('storage', (e) => {
    if (
      e.key === SERVICES_STORAGE_KEY ||
      e.key === BUNDLES_STORAGE_KEY ||
      e.key === CATEGORIES_STORAGE_KEY
    ) {
      callback();
    }
  });
  return () => {
    window.removeEventListener(CATALOG_UPDATE_EVENT, handler);
  };
}

// Automatic background sync initialization on browser load & tab refocus
if (typeof window !== 'undefined') {
  // Sync on startup after a minimal delay so UI renders instantly
  setTimeout(() => {
    syncAllCatalogFromRemote().catch(() => {});
  }, 250);

  // Sync whenever the user returns to or focuses the window/tab
  window.addEventListener('focus', () => {
    syncAllCatalogFromRemote().catch(() => {});
  });
}
